import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Edit, RotateCcw, FileDown, CopyPlus } from 'lucide-react';
import { GenericListView, PrimaryButton, DisciplinePill, getDisciplinePillClassName, DocStatusPill, getDocStatusConfig, getDocStatusPillClassName } from '../../common';
import FormPageLayout from '../../common/FormPageLayout';
import type { ActionButtonConfig } from '../../common/FormPageLayout';
import type { AssessmentFormHandle } from './NursingAssessmentForm';
import ErrorDialog from '../../common/ErrorDialog';
import ReasonDialog from '../../common/ReasonDialog';
import ConfirmDialog from '../../common/ConfirmDialog';
import AutosaveIndicator from '../../common/AutosaveIndicator';
import VersionBadge from '../../common/VersionBadge';
import { useToast } from '../../common/Toast';
import { useAutosave } from '../../../hooks/useAutosave';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { useDirtySnapshot } from '../../../hooks/useDirtySnapshot';
import { isApiError } from '../../../dtos/common/api.dto';
import { describeApiError, getApiErrorStatus, getMissingFields, hasApiErrorCode } from '../../../utils/apiErrors';
import type { PermissionString } from '../../../types/permissions';
import NursingAssessmentForm from './NursingAssessmentForm';
import PTAssessmentForm from './PTAssessmentForm';
import OTAssessmentForm from './OTAssessmentForm';
import STAssessmentForm from './STAssessmentForm';
import TemplateAssessmentForm from '../templates/TemplateAssessmentForm';
import { templateDisciplines, templateFor } from '../templates/templateUtils';
import { disciplineLongLabel, getDisciplineColors } from '../../../constants/disciplines';
import { useServicesOffered } from '../../../hooks/useServicesOffered';
import { useHasAnyPermission } from '../../../hooks/usePermission';
import { 
  AssessmentsService, 
  AssessmentListItem, 
  AssessmentDiscipline, 
  AssessmentStatus, 
  AssessmentDetail 
} from '../../../services/patientmr/assessments.service';

interface AssessmentsProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId?: string;
  visitId?: string;
  provider?: string;
}

// Utility function to extract provider name from provider object or string
const extractProviderName = (provider: any): string => {
  if (typeof provider === 'string') {
    return provider;
  }
  
  if (provider && typeof provider === 'object') {
    // Try different combinations of name fields
    if (provider.firstName && provider.lastName) {
      return `${provider.firstName} ${provider.lastName}`;
    }
    if (provider.name) {
      return provider.name;
    }
    if (provider.email) {
      return provider.email;
    }
    if (provider.displayName) {
      return provider.displayName;
    }
  }
  
  return 'Unknown Provider';
};

const Assessments: React.FC<AssessmentsProps> = (props) => {
  const servicesOffered = useServicesOffered();
  const { expanded, toggleExpanded, patientId, visitId, provider } = props;

  // Check if user has permission to create assessments
  const canCreateAssessment = useHasAnyPermission([
    'patientmr:phi:assessment:create' as any,
    'patientmr:phi:assessment:createall' as any
  ]);

  // Check if user has permission to read assessments
  const canReadAssessment = useHasAnyPermission([
    'patientmr:phi:assessment:read' as any,
    'patientmr:phi:assessment:readall' as any
  ]);

  // Check if user has permission to update assessments
  const canUpdateAssessment = useHasAnyPermission([
    'patientmr:phi:assessment:update' as any,
    'patientmr:phi:assessment:updateall' as any
  ]);

  // Read-only mode: has read but no write permissions
  const isReadOnlyView = canReadAssessment && !canCreateAssessment && !canUpdateAssessment;

  // QA reviewers / supervisors can start QA on a submitted assessment (Phase 4, §7A)
  const canQAAssessment = useHasAnyPermission([
    'patientmr:qa' as PermissionString,
    'patientmr:phi:supervisor:finalize' as PermissionString,
    'patientmr:phi:supervisor:approve' as PermissionString
  ]);

  // Active tabs: 'past', 'new', or 'select_type'
  const [activeTab, setActiveTab] = useState('past');
  
  // Selected assessment type
  const [selectedAssessmentType, setSelectedAssessmentType] = useState<string | null>(null);

  // State management for assessments
  const [assessments, setAssessments] = useState<AssessmentListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false); // Track if we've loaded data at least once
  const [saving, setSaving] = useState<boolean>(false); // Track save/update operations
  const [submitting, setSubmitting] = useState<boolean>(false); // Track submit operations
  const [reopening, setReopening] = useState<boolean>(false); // Track reopen operations
  
  // Error dialog state for save failures
  const [saveErrorDialogOpen, setSaveErrorDialogOpen] = useState<boolean>(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string>('');
  const [saveErrorDetails, setSaveErrorDetails] = useState<string>('');
  const [lastFormData, setLastFormData] = useState<Record<string, unknown> | null>(null); // Store form data for retry
  
  // Pagination state
  const [hasMoreAssessments, setHasMoreAssessments] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [paginationToken, setPaginationToken] = useState<string | undefined>(undefined);
  const ASSESSMENTS_PER_PAGE = 6;
  
  // State for current assessment details when editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<AssessmentDetail | null>(null);
  
  // State for manual refresh functionality
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // Ref for deduplication
  const hasLoadedAssessmentsRef = useRef<string | null>(null);
  const formRef = useRef<AssessmentFormHandle>(null);

  const { showToast } = useToast();

  // Phase 4: reason prompts, QA start, read-only past versions, submit field list
  const [reasonDialog, setReasonDialog] = useState<{ kind: 'reopen' | 'reject'; assessmentId: string } | null>(null);
  const [startingQA, setStartingQA] = useState<boolean>(false);
  const [viewingVersion, setViewingVersion] = useState<{ version: number; detail: AssessmentDetail } | null>(null);
  const [loadingVersion, setLoadingVersion] = useState<boolean>(false);
  const [submitBlock, setSubmitBlock] = useState<{ message: string; details: string } | null>(null);

  // Dirty tracking + autosave + unsaved-changes guard (ASM-03)
  const dirty = useDirtySnapshot(() => formRef.current?.getSnapshot());
  const editingStatus = editingData?.status || AssessmentStatus.DRAFT;
  const canEditCurrent = canCreateAssessment || canUpdateAssessment;
  const autosaveEnabled =
    !!editingId && !!patientId && activeTab === 'new' && editingStatus === AssessmentStatus.DRAFT && canEditCurrent && !viewingVersion;

  const autosave = useAutosave({
    enabled: autosaveEnabled,
    isDirty: dirty.isDirty,
    resetKey: `${editingId ?? 'new'}:${editingStatus}`,
    save: async () => {
      if (!editingId || !patientId || !formRef.current) return;
      const snapshot = dirty.takeSnapshot();
      const disciplineType = mapCategoryToDiscipline(selectedAssessmentType);
      const assessmentData = AssessmentsService.mapFormDataToAssessment(formRef.current.getSaveData(), disciplineType, provider);
      await AssessmentsService.updateAssessment(patientId, editingId, { ...assessmentData, assessmentId: editingId });
      dirty.markClean(snapshot);
    },
    onError: (err, stopped) => {
      showToast(
        stopped
          ? `Autosave paused: ${describeApiError(err)}`
          : describeApiError(err, 'Autosave failed. Your changes are not saved yet.'),
        { variant: stopped ? 'warning' : 'error' }
      );
    },
  });

  const unsavedGuard = useUnsavedChangesGuard(dirty.isDirty);

  const replaceListStatus = (assessmentId: string, status: AssessmentStatus) => {
    setAssessments(prev =>
      prev.map(item => (item.assessmentId === assessmentId ? { ...item, status } : item))
    );
  };

  // Save assessment from the form as a DRAFT. Returns the assessment id, or null on failure.
  const handleSaveAssessment = async (formData: Record<string, unknown>): Promise<string | null> => {
    // Store form data for potential retry
    setLastFormData(formData);
    const snapshot = dirty.takeSnapshot();

    try {
      setSaving(true);
      setError(null);

      // Check if we have the required IDs
      if (!patientId) {
        throw new Error('Patient ID is required to save an assessment');
      }

      // Map the form data to the assessment structure
      const disciplineType = mapCategoryToDiscipline(selectedAssessmentType);
      const assessmentData = AssessmentsService.mapFormDataToAssessment(formData, disciplineType, provider);
      let savedId: string | null = null;

      if (editingId) {
        // Update existing assessment
        const updatedAssessment = await AssessmentsService.updateAssessment(
          patientId,
          editingId,
          {
            ...assessmentData,
            assessmentId: editingId
          }
        );
        savedId = editingId;

        // Update the local state
        setAssessments(prevAssessments =>
          prevAssessments.map(assessment =>
            assessment.assessmentId === editingId
              ? {
                  ...assessment,
                  assessmentId: updatedAssessment.assessmentId || editingId,
                  date: new Date().toISOString(),
                  status: updatedAssessment.status || AssessmentStatus.DRAFT,
                  discipline: updatedAssessment.discipline,
                  providerName: extractProviderName(updatedAssessment.provider)
                }
              : assessment
          )
        );
        dirty.markClean(snapshot);
      } else {
        // Create new assessment
        const newAssessment = await AssessmentsService.createAssessment(
          patientId,
          visitId || 'NO_VISIT',
          assessmentData
        );

        // Add to the local state
        if (newAssessment.assessmentId) {
          savedId = newAssessment.assessmentId;
          // Ensure providerName is a string - handle cases where provider might be an object
          const providerName = extractProviderName(newAssessment.provider);

          const newListItem: AssessmentListItem = {
            assessmentId: newAssessment.assessmentId,
            date: new Date().toISOString(),
            status: newAssessment.status || AssessmentStatus.DRAFT,
            discipline: newAssessment.discipline,
            providerName: providerName
          };

          setAssessments(prev => [newListItem, ...prev]);

          // Set editingId and editingData so user can continue to submit the newly created assessment.
          // The form remounts with the saved data, so dirty tracking starts again.
          dirty.reset();
          setEditingId(newAssessment.assessmentId);
          setEditingData(newAssessment);
        }
      }

      autosave.markSaved();
      // Clear stored form data on success
      setLastFormData(null);
      return savedId;
    } catch (err: unknown) {
      console.error('Error saving assessment');

      // Determine error message
      let errorMessage = 'Failed to save assessment. Please try again.';
      let errorDetails = '';
      const message = err instanceof Error ? err.message : '';

      if (isApiError(err)) {
        errorMessage = describeApiError(err, errorMessage);
        if (getApiErrorStatus(err) === 409) {
          // The record is no longer a draft; nothing to retry
          setLastFormData(null);
        }
      } else if (message.includes('No active visit found')) {
        errorMessage = 'Cannot save assessment: No active visit found.';
        errorDetails = 'Please start a visit first before saving the assessment.';
      } else if (message.includes('Patient ID is required')) {
        errorMessage = 'Cannot save assessment: Patient information is missing.';
        errorDetails = 'Please ensure a patient is selected.';
      } else if (message) {
        errorDetails = message;
      }

      // Show error dialog instead of inline error
      setSaveErrorMessage(errorMessage);
      setSaveErrorDetails(errorDetails);
      setSaveErrorDialogOpen(true);

      // Also set the inline error for the list view
      setError(errorMessage);
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Retry save with stored form data
  const handleRetrySave = () => {
    if (lastFormData) {
      handleSaveAssessment(lastFormData);
    }
  };

  // Open an assessment (edit when DRAFT, read-only otherwise)
  // Phase 19 F16: printable assessment
  const handleDownloadAssessmentPdf = async (assessmentId: string) => {
    if (!patientId) return;
    try {
      const { url } = await AssessmentsService.getAssessmentPdfUrl(patientId, assessmentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(describeApiError(err, 'Could not prepare the assessment PDF.'));
    }
  };

  // Phase 19 F14: a reassessment starts from the completed assessment (visit-specific sections blank)
  const handleStartReassessment = async (source: AssessmentListItem) => {
    if (!patientId || !source.assessmentId) return;
    try {
      const created = await AssessmentsService.createAssessment(
        patientId,
        visitId || 'NO_VISIT',
        { provider: provider || '', discipline: source.discipline, data: { formConfig: { assessmentType: 'recertification' } } } as AssessmentDetail,
        { copyFromId: source.assessmentId }
      );
      showToast('Reassessment started from the completed assessment. Update what changed, then submit.', { variant: 'success' });
      if (created.assessmentId) await handleEditAssessment(created.assessmentId);
    } catch (err) {
      setError(describeApiError(err, 'Could not start the reassessment.'));
    }
  };

  const handleEditAssessment = async (assessmentId: string) => {
    try {
      setLoading(true);
      setError(null); // Clear any existing errors

      // Fetch the assessment details
      const assessmentDetail = await AssessmentsService.getAssessmentDetails(patientId, assessmentId);

      // Only switch to edit mode if we successfully got the assessment details
      dirty.reset();
      setViewingVersion(null);
      setEditingId(assessmentId);
      setEditingData(assessmentDetail);

      // Map discipline to UI category
      const category = mapDisciplineToCategory(assessmentDetail.discipline);
      setSelectedAssessmentType(category);
      setActiveTab('new');
    } catch (err: unknown) {
      console.error('Error starting assessment edit');
      setError(describeApiError(err, 'Failed to load assessment for editing. Please try again.'));

      // Reload the assessments list when edit fails
      await reloadAssessmentsList('edit failure');
    } finally {
      setLoading(false);
    }
  };

  // Submit: validate on the client, save pending edits, then submit (server re-checks required fields)
  const handleSubmitClick = async () => {
    const form = formRef.current;
    if (!form) return;

    const validationErrors = form.validate();
    if (validationErrors.length > 0) {
      setSubmitBlock({
        message: 'Complete the required fields before submitting. Your changes can still be saved as a draft.',
        details: validationErrors.join('\n'),
      });
      return;
    }

    let assessmentId = editingId;
    if (!assessmentId || dirty.isDirty()) {
      assessmentId = await handleSaveAssessment(form.getSaveData());
    }
    if (assessmentId) {
      await handleSubmitAssessment(assessmentId);
    }
  };

  // Submit assessment
  const handleSubmitAssessment = async (assessmentId: string) => {
    try {
      setSubmitting(true);
      setError(null);

      // Call service to submit the assessment
      await AssessmentsService.submitAssessment(patientId, assessmentId);

      replaceListStatus(assessmentId, AssessmentStatus.SUBMITTED);

      // Update editing data if this is the currently edited assessment
      setEditingData(prev =>
        prev && (prev.assessmentId === assessmentId || editingId === assessmentId)
          ? { ...prev, status: AssessmentStatus.SUBMITTED }
          : prev
      );
      showToast('Assessment submitted.', { variant: 'success' });
    } catch (err: unknown) {
      console.error('Error submitting assessment');

      const missing = getMissingFields(err);
      if (hasApiErrorCode(err, 'REQUIRED_FIELDS_MISSING')) {
        setSubmitBlock({
          message: 'The server found required fields that are still empty.',
          details: missing.length > 0 ? missing.join('\n') : describeApiError(err),
        });
      } else {
        showToast(describeApiError(err, 'Failed to submit assessment. Please try again.'), { variant: 'error' });
        // Reload the assessments list when submit fails
        await reloadAssessmentsList('submit failure');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Reopen (from COMPLETE) or reject (from SUBMITTED/QA); the reason comes from the ReasonDialog
  const handleReopenAssessment = async (assessmentId: string, reason: string) => {
    try {
      setReopening(true);
      setError(null);

      // Call service to reopen the assessment
      await AssessmentsService.reopenAssessment(patientId, assessmentId, reason);
      setReasonDialog(null);

      replaceListStatus(assessmentId, AssessmentStatus.DRAFT);

      // Reload the open record so the new version number and reopen history show
      if (editingId === assessmentId) {
        try {
          const fresh = await AssessmentsService.getAssessmentDetails(patientId, assessmentId);
          setEditingData(fresh);
        } catch {
          setEditingData(prev => (prev ? { ...prev, status: AssessmentStatus.DRAFT } : prev));
        }
        setViewingVersion(null);
      }
      showToast('Assessment reopened as a draft.', { variant: 'success' });
    } catch (err: unknown) {
      console.error('Error reopening assessment');
      showToast(describeApiError(err, 'Failed to reopen assessment. Please try again.'), { variant: 'error' });

      // Reload the assessments list when reopen fails
      await reloadAssessmentsList('reopen failure');
    } finally {
      setReopening(false);
    }
  };

  // Start QA review (SUBMITTED -> QA)
  const handleStartQA = async (assessmentId: string) => {
    try {
      setStartingQA(true);
      await AssessmentsService.startQAAssessment(patientId, assessmentId);
      replaceListStatus(assessmentId, AssessmentStatus.QA);
      setEditingData(prev => (prev && editingId === assessmentId ? { ...prev, status: AssessmentStatus.QA } : prev));
      showToast('QA review started.', { variant: 'success' });
    } catch (err: unknown) {
      console.error('Error starting assessment QA');
      showToast(describeApiError(err, 'Failed to start QA. Please try again.'), { variant: 'error' });
    } finally {
      setStartingQA(false);
    }
  };

  // Approve/finalize assessment
  const handleApproveAssessment = async (assessmentId: string) => {
    try {
      setSubmitting(true);
      setError(null);

      // Call service to finalize the assessment
      await AssessmentsService.finalizeAssessment(patientId, assessmentId);

      replaceListStatus(assessmentId, AssessmentStatus.COMPLETE);

      // Close the editing form after approval and return to the list
      dirty.reset();
      setEditingId(null);
      setEditingData(null);
      setViewingVersion(null);
      setActiveTab('past');
      setSelectedAssessmentType(null);
      showToast('Assessment approved.', { variant: 'success' });
    } catch (err: unknown) {
      console.error('Error approving assessment');
      // SELF_APPROVAL, INVALID_TRANSITION, ... are shown as a toast; the form stays open
      showToast(describeApiError(err, 'Failed to approve assessment. Please try again.'), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Load a read-only earlier version (GET ?version=n)
  const handleViewVersion = async (version: number) => {
    if (!editingId || !patientId) return;
    if (editingData?.version && version >= editingData.version) {
      setViewingVersion(null);
      return;
    }
    try {
      setLoadingVersion(true);
      const detail = await AssessmentsService.getAssessmentDetails(patientId, editingId, version);
      setViewingVersion({ version, detail });
    } catch (err: unknown) {
      console.error('Error loading assessment version');
      showToast(describeApiError(err, 'Could not load that version.'), { variant: 'error' });
    } finally {
      setLoadingVersion(false);
    }
  };
  
  // Memoize getInitialData to prevent form resets during re-renders
  const getInitialData = useCallback(() => {
    if (viewingVersion) {
      // Read-only snapshot of an earlier version
      return AssessmentsService.mapAssessmentToFormData(viewingVersion.detail);
    }
    if (editingData) {
      // When editing, use the service to map the assessment data to form data
      return AssessmentsService.mapAssessmentToFormData(editingData);
    }
    // A new assessment starts from the form's own defaults (Phase 19 F26: stale data templates removed)
    return null;
  }, [editingData, viewingVersion]);
  
  // Map discipline enum to UI category string
  const mapDisciplineToCategory = (discipline: AssessmentDiscipline): string => {
    switch (discipline) {
      case AssessmentDiscipline.RN:
        return "Nursing";
      case AssessmentDiscipline.PT:
        return "PT";
      case AssessmentDiscipline.OT:
        return "OT";
      case AssessmentDiscipline.ST:
        return "ST";
      default:
        // Phase 20B: a template discipline's category is its code (MSW)
        return templateFor(discipline, 'assessment') ? String(discipline) : "Unknown";
    }
  };
  
  // Map UI category string to discipline enum
  const mapCategoryToDiscipline = (category: string | null): AssessmentDiscipline => {
    switch (category) {
      case "Nursing":
        return AssessmentDiscipline.RN;
      case "PT":
        return AssessmentDiscipline.PT;
      case "OT":
        return AssessmentDiscipline.OT;
      case "ST":
        return AssessmentDiscipline.ST;
      default:
        // Phase 20B: template disciplines use their code as the category
        if (category && templateFor(category, 'assessment')) return category as AssessmentDiscipline;
        return AssessmentDiscipline.RN; // Default case
    }
  };
  
  // Handle assessment type selection
  const handleSelectAssessmentType = (type: string) => {
    dirty.reset();
    setViewingVersion(null);
    setEditingId(null);
    setEditingData(null);
    setSelectedAssessmentType(type);
    setActiveTab('new');
  };
  
  // Handle cancel from form (asks first when there are unsaved changes)
  const closeForm = () => {
    dirty.reset();
    setViewingVersion(null);
    setEditingId(null);
    setEditingData(null);
    setSelectedAssessmentType(null);
    setSaving(false); // Reset saving state when cancelled
    setLastFormData(null); // Clear stored form data
    setActiveTab('past');
  };
  const handleCancelForm = () => unsavedGuard.confirmNavigation(closeForm);

  const buildAssessmentActions = (): ActionButtonConfig[] => {
    const currentStatus = editingData?.status || AssessmentStatus.DRAFT;
    const anyBusy = saving || submitting || reopening || startingQA;

    // Determine if user is in read-only mode (has read but not create permission)
    const isReadOnly = !canCreateAssessment && canReadAssessment;

    // Viewing an earlier version: read-only, the only action is going back
    if (viewingVersion) {
      return [
        {
          key: 'back-to-current',
          label: 'Back to current version',
          onClick: () => setViewingVersion(null),
          variant: 'secondary',
        },
      ];
    }

    return [
      {
        key: 'cancel',
        label: 'Close',
        onClick: handleCancelForm,
        variant: 'secondary',
        disabled: anyBusy,
      },
      {
        key: 'save',
        label: 'Save',
        loadingLabel: 'Saving...',
        onClick: () => formRef.current?.triggerSave(),
        variant: 'secondary',
        loading: saving,
        disabled: anyBusy,
        // Hide in read-only mode or when not in DRAFT status
        hidden: isReadOnly || currentStatus !== AssessmentStatus.DRAFT,
      },
      {
        key: 'submit',
        label: 'Submit',
        loadingLabel: 'Submitting...',
        onClick: () => { void handleSubmitClick(); },
        variant: 'primary',
        loading: submitting,
        disabled: anyBusy,
        // Hide in read-only mode or when not in DRAFT status
        hidden: isReadOnly || currentStatus !== AssessmentStatus.DRAFT,
      },
      {
        key: 'reject',
        label: 'Reject',
        loadingLabel: 'Rejecting...',
        onClick: editingId ? () => setReasonDialog({ kind: 'reject', assessmentId: editingId }) : () => {},
        variant: 'danger',
        loading: reopening,
        disabled: anyBusy,
        // Hide in read-only mode
        // Phase 12 (§4.4): QA decision, QA reviewers only
        hidden:
          isReadOnly ||
          !canQAAssessment ||
          (currentStatus !== AssessmentStatus.SUBMITTED &&
          currentStatus !== AssessmentStatus.QA),
      },
      {
        key: 'startQA',
        label: 'Start QA',
        loadingLabel: 'Starting QA...',
        onClick: editingId ? () => handleStartQA(editingId) : () => {},
        variant: 'warning',
        loading: startingQA,
        disabled: anyBusy,
        // QA reviewers only, and only from SUBMITTED
        hidden: isReadOnly || !canQAAssessment || currentStatus !== AssessmentStatus.SUBMITTED,
      },
      {
        key: 'approve',
        label: 'Approve',
        loadingLabel: 'Approving...',
        onClick: editingId ? () => handleApproveAssessment(editingId) : () => {},
        variant: 'success',
        loading: submitting,
        disabled: anyBusy,
        // Hide in read-only mode
        // Phase 12 (§4.4): QA decision, QA reviewers only
        hidden:
          isReadOnly ||
          !canQAAssessment ||
          (currentStatus !== AssessmentStatus.SUBMITTED &&
          currentStatus !== AssessmentStatus.QA),
      },
      {
        key: 'reopen',
        label: 'Reopen',
        loadingLabel: 'Reopening...',
        onClick: editingId ? () => setReasonDialog({ kind: 'reopen', assessmentId: editingId }) : () => {},
        variant: 'primary',
        loading: reopening,
        disabled: anyBusy,
        // Hide in read-only mode
        hidden: isReadOnly || !canQAAssessment || currentStatus !== AssessmentStatus.COMPLETE,
      },
    ];
  };

  const getFormTitle = (): string => {
    const titleMap: Record<string, string> = {
      Nursing: 'Nursing Assessment',
      PT: 'Physical Therapy Assessment',
      OT: 'Occupational Therapy Assessment',
      ST: 'Speech Therapy Assessment',
    };
    return titleMap[selectedAssessmentType ?? ''] ?? 'Assessment';
  };

  // Manual refresh function
  const handleRefreshAssessments = async () => {
    if (!patientId || refreshing) return;
    
    try {
      setRefreshing(true);
      setError(null);
      console.log(`Manually refreshing assessments for patient ${patientId}`);
      
      // Reset ref to allow reload
      hasLoadedAssessmentsRef.current = null;
      
      const response = await AssessmentsService.getAssessments(patientId, {
        limit: ASSESSMENTS_PER_PAGE.toString()
      });
      setAssessments(response.assessments);
      setHasMoreAssessments(response.hasMore);
      setPaginationToken(response.paginationToken);
      setHasLoadedOnce(true); // Mark that we've successfully loaded data
      
      // Set ref after successful refresh
      hasLoadedAssessmentsRef.current = patientId;
    } catch (err: any) {
      console.error('Error refreshing assessments:', err);
      setError('Failed to refresh assessments. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };
  
  // Load more assessments
  const handleLoadMoreAssessments = async () => {
    if (!patientId || !paginationToken || loadingMore) return;
    
    try {
      setLoadingMore(true);
      setError(null);
      
      const response = await AssessmentsService.getAssessments(patientId, {
        limit: ASSESSMENTS_PER_PAGE.toString(),
        paginationToken: paginationToken
      });
      
      // Append new assessments to existing ones
      setAssessments(prev => [...prev, ...response.assessments]);
      setHasMoreAssessments(response.hasMore);
      setPaginationToken(response.paginationToken);
    } catch (err: any) {
      console.error('Error loading more assessments:', err);
      setError('Failed to load more assessments. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };
  
  // Helper function to reload assessments list after operation failures
  const reloadAssessmentsList = async (context: string) => {
    if (!patientId) {
      console.log(`Cannot reload assessments: No patientId provided (context: ${context})`);
      return;
    }
    
    try {
      console.log(`Reloading assessments list after ${context}...`);
      const response = await AssessmentsService.getAssessments(patientId, {
        limit: ASSESSMENTS_PER_PAGE.toString()
      });
      setAssessments(response.assessments);
      setHasMoreAssessments(response.hasMore);
      setPaginationToken(response.paginationToken);
      setHasLoadedOnce(true); // Mark that we've successfully loaded data
      console.log(`Successfully reloaded ${response.assessments.length} assessments after ${context}`);
    } catch (reloadErr: any) {
      console.error(`Error reloading assessments after ${context}:`, reloadErr);
      // Don't overwrite the original error message, but add additional context
      setError(prevError => `${prevError} Additionally, failed to refresh the assessment list.`);
    }
  };
  
  // getCategoryColor removed — use getDisciplinePillClassName(discipline) from common/DisciplinePill
  
  // getStatusDisplayText / getStatusColor removed — use DocStatusPill / getDocStatusConfig from common/Pills

  // Get assessment type display text
  const getAssessmentTypeDisplayText = (type: string | undefined): string => {
    if (!type) return '-';

    const lowerType = type.toLowerCase();

    switch (lowerType) {
      case 'start_of_care':
      case 'initial':
        return 'Start of Care';
      case 'reassessment':
      case 'recertification':
        return 'Recertification';
      case 'end_of_care':
      case 'discharge':
        return 'Discharge';
      default:
        return type; // Return as-is for unknown types
    }
  };

  // Get assessment type color for badge
  const getAssessmentTypeColor = (type: string | undefined): string => {
    if (!type) return 'bg-gray-100 text-gray-700';

    const lowerType = type.toLowerCase();

    switch (lowerType) {
      case 'start_of_care':
      case 'initial':
        return 'bg-blue-100 text-blue-700';
      case 'reassessment':
      case 'recertification':
        return 'bg-yellow-100 text-yellow-700';
      case 'end_of_care':
      case 'discharge':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  
  const editingAssessmentType = editingData?.data?.formConfig?.assessmentType || editingData?.data?.assessmentType;
  const formSubtitle = editingAssessmentType
    ? getAssessmentTypeDisplayText(editingAssessmentType)
    : undefined;

  const currentVersion = editingData?.version ?? 1;
  const formHeaderRight = (
    <div className="flex items-center gap-2 flex-wrap justify-end">
      {!viewingVersion && autosaveEnabled && (
        <AutosaveIndicator
          saving={autosave.saving}
          lastSavedAt={autosave.lastSavedAt}
          stopped={autosave.stopped}
          hasError={!!autosave.error}
        />
      )}
      {viewingVersion ? (
        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
          Viewing v{viewingVersion.version} (read-only)
        </span>
      ) : (
        <VersionBadge version={editingData?.version} />
      )}
      {editingId && currentVersion > 1 && (
        <label className="flex items-center gap-1 text-xs text-slate-600">
          <span className="sr-only">View version</span>
          <select
            className="border border-slate-300 rounded px-1 py-0.5 text-xs bg-white"
            value={viewingVersion ? String(viewingVersion.version) : String(currentVersion)}
            disabled={loadingVersion}
            onChange={(e) => { void handleViewVersion(Number(e.target.value)); }}
            aria-label="View version"
          >
            {Array.from({ length: currentVersion }, (_, i) => currentVersion - i).map((v) => (
              <option key={v} value={v}>
                {v === currentVersion ? `v${v} (current)` : `View v${v}`}
              </option>
            ))}
          </select>
        </label>
      )}
      <DocStatusPill status={viewingVersion?.detail.status || editingData?.status || AssessmentStatus.DRAFT} />
    </div>
  );

  // Render the appropriate assessment form based on type
  const renderAssessmentForm = () => {
    if (!selectedAssessmentType) return null;

    // Case C: User lacks read permissions - show permission error
    if (!canReadAssessment) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-center max-w-md">
            <div className="text-red-500 text-4xl mb-4">🔒</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Not enough permissions to view form</h3>
            <p className="text-gray-600 text-sm">
              You don't have the required permissions to view assessment forms. Contact your administrator if you need access.
            </p>
          </div>
        </div>
      );
    }

    // Determine if form should be read-only
    // Case B: User can read but not create - form is read-only
    // Phase 4: only a DRAFT can be edited; past versions are always read-only
    const isReadOnly =
      !canCreateAssessment ||
      !!viewingVersion ||
      (!!editingId && (editingData?.status || AssessmentStatus.DRAFT) !== AssessmentStatus.DRAFT);
    const formKey = viewingVersion ? `${editingId}-v${viewingVersion.version}` : (editingId || 'new');
    const formStatus = viewingVersion?.detail.status ?? editingData?.status;

    // Get the initial data using the memoized function
    const initialData = getInitialData();

    switch (selectedAssessmentType) {
      case "Nursing":
        return (
          <NursingAssessmentForm
            ref={formRef}
            key={formKey}
            editingId={editingId}
            patientId={patientId || ''}
            existingData={editingData ? initialData : null}
            onSave={handleSaveAssessment}
            assessmentStatus={formStatus}
            isReadOnly={isReadOnly}
          />
        );
      case "PT":
        return (
          <PTAssessmentForm
            ref={formRef}
            key={formKey}
            editingId={editingId}
            patientId={patientId || ''}
            existingData={editingData ? initialData : null}
            onSave={handleSaveAssessment}
            assessmentStatus={formStatus}
            isReadOnly={isReadOnly}
          />
        );
      case "OT":
        return (
          <OTAssessmentForm
            ref={formRef}
            key={formKey}
            editingId={editingId}
            patientId={patientId || ''}
            existingData={editingData ? initialData : null}
            onSave={handleSaveAssessment}
            assessmentStatus={formStatus}
            isReadOnly={isReadOnly}
          />
        );
      case "ST":
        return (
          <STAssessmentForm
            ref={formRef}
            key={formKey}
            editingId={editingId}
            patientId={patientId || ''}
            existingData={editingData ? initialData : null}
            onSave={handleSaveAssessment}
            assessmentStatus={formStatus}
            isReadOnly={isReadOnly}
          />
        );
      default: {
        // Phase 20B: disciplines without a hand-built form use their clinical template
        const template = templateFor(selectedAssessmentType, 'assessment');
        if (!template) return null;
        return (
          <TemplateAssessmentForm
            ref={formRef}
            key={formKey}
            template={template}
            editingId={editingId}
            patientId={patientId || ''}
            existingData={editingData ? initialData : null}
            onSave={handleSaveAssessment}
            assessmentStatus={formStatus}
            isReadOnly={isReadOnly}
          />
        );
      }
    }
  };
  
  // NOTE: This selection screen is now bypassed with the dropdown menu
  // but kept for future reference or alternative UI flows
  const renderAssessmentTypeSelection = () => {
    const assessmentTypes = [
      { type: "Nursing", color: "bg-purple-100 text-purple-800 border-purple-200" },
      { type: "PT", color: "bg-blue-100 text-blue-800 border-blue-200" },
      { type: "OT", color: "bg-green-100 text-green-800 border-green-200" },
      { type: "ST", color: "bg-orange-100 text-orange-800 border-orange-200" }
    ];
    
    return (
      <div className="py-6">
        <h3 className="text-lg font-medium mb-6 text-center">Select Assessment Type</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
          {assessmentTypes.map((item) => (
            <button
              key={item.type}
              className={`p-6 rounded-lg border-2 ${item.color} hover:opacity-90 transition-opacity flex flex-col items-center justify-center`}
              onClick={() => handleSelectAssessmentType(item.type)}
            >
              <span className="text-xl font-medium">{item.type}</span>
              <span className="text-sm mt-2">{item.type === "Nursing" ? "Nursing Assessment" : 
                item.type === "PT" ? "Physical Therapy Assessment" :
                item.type === "OT" ? "Occupational Therapy Assessment" :
                "Speech Therapy Assessment"}
              </span>
            </button>
          ))}
        </div>
        <div className="flex justify-center mt-6">
          <PrimaryButton
            className="border border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
            onClick={() => setActiveTab('past')}
          >
            Cancel
          </PrimaryButton>
        </div>
      </div>
    );
  };
  
  // Fetch assessments when the component mounts or patientId changes
  useEffect(() => {
    const fetchAssessments = async () => {
      if (!patientId) {
        console.warn('No patientId provided to assessments component');
        setError('Patient information not available. Please select a patient to view assessments.');
        return;
      }

      // Skip if already loaded for this patient
      if (hasLoadedAssessmentsRef.current === patientId || loading) {
        return;
      }

      hasLoadedAssessmentsRef.current = patientId;
      
      try {
        setLoading(true);
        setError(null);
        console.log(`Fetching assessments for patient ${patientId}`);
        const response = await AssessmentsService.getAssessments(patientId, {
          limit: ASSESSMENTS_PER_PAGE.toString()
        });
        console.log(`Loaded ${response.assessments.length} assessments for patient ${patientId}`);
        setAssessments(response.assessments);
        setHasMoreAssessments(response.hasMore);
        setPaginationToken(response.paginationToken);
        setHasLoadedOnce(true); // Mark that we've successfully loaded data
      } catch (err: any) {
        console.error('Error fetching assessments:', err);
        
        if (err.response && err.response.status === 401) {
          setError('Authentication error. Please log in again.');
        } else if (err.response && err.response.status >= 500) {
          setError('Server error. Please try again later.');
        } else {
          setError('Failed to load assessments. Please try again.');
        }
        // Reset ref on error to allow retry
        hasLoadedAssessmentsRef.current = null;
      } finally {
        setLoading(false);
      }
    };

    // Load assessments when the tab is expanded (opened)
    if (expanded) {
      fetchAssessments();
    }
    
    // Reset state when collapsed
    if (!expanded) {
      setActiveTab('past');
      setSelectedAssessmentType(null);
      setEditingId(null);
      setEditingData(null);
      setError(null);
      setHasLoadedOnce(false); // Reset load state when collapsed
      setSaving(false); // Reset saving state when collapsed
      setLastFormData(null); // Clear stored form data
      setPaginationToken(undefined); // Reset pagination
      setHasMoreAssessments(false);
      hasLoadedAssessmentsRef.current = null; // Reset ref when collapsed
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loading is only a re-entry guard; adding it would re-run the load when loading toggles
  }, [patientId, expanded]);

  // Assessment type options for dropdown
  const assessmentTypeOptions = [
    {
      label: 'Private Duty Nursing',
      color: 'bg-blue-500',
      onClick: () => handleSelectAssessmentType('Nursing'),
    },
    {
      label: 'Physical Therapy',
      color: 'bg-green-500',
      onClick: () => handleSelectAssessmentType('PT'),
    },
    {
      label: 'Occupational Therapy',
      color: 'bg-orange-500',
      onClick: () => handleSelectAssessmentType('OT'),
    },
    {
      label: 'Speech Therapy',
      color: 'bg-purple-500',
      onClick: () => handleSelectAssessmentType('ST'),
    },
    // Phase 20B: template assessments of the disciplines the agency offers (MSW)
    ...templateDisciplines('assessment').filter(d => servicesOffered.includes(d)).map(d => ({
      label: disciplineLongLabel(d),
      color: getDisciplineColors(d).dot,
      onClick: () => handleSelectAssessmentType(d),
    })),
  ];

  // Sort assessments by date
  const sortedAssessments = [...assessments].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return (
    <>
      {/* Error Dialog for Save Failures */}
      <ErrorDialog
        open={saveErrorDialogOpen}
        onClose={() => setSaveErrorDialogOpen(false)}
        title="Save Failed"
        message={saveErrorMessage}
        details={saveErrorDetails}
        onRetry={lastFormData ? handleRetrySave : undefined}
        retryLabel="Try Again"
      />

      {/* Submit blocked: required fields (client check or server 422 list) */}
      <ErrorDialog
        open={submitBlock !== null}
        onClose={() => setSubmitBlock(null)}
        title="Cannot submit yet"
        message={submitBlock?.message || ''}
        details={submitBlock?.details}
      />

      {/* Reopen / reject: reason required (Phase 4) */}
      <ReasonDialog
        open={reasonDialog !== null}
        title={reasonDialog?.kind === 'reject' ? 'Reject assessment' : 'Reopen assessment'}
        description={
          reasonDialog?.kind === 'reject'
            ? 'The assessment goes back to the author as a draft. The reason is kept in its history.'
            : 'The assessment returns to draft and a new version is started. The reason is kept in its history.'
        }
        label={reasonDialog?.kind === 'reject' ? 'Reason for rejecting' : 'Reason for reopening'}
        confirmLabel={reasonDialog?.kind === 'reject' ? 'Reject' : 'Reopen'}
        destructive={reasonDialog?.kind === 'reject'}
        busy={reopening}
        onConfirm={(reason) => (reasonDialog ? handleReopenAssessment(reasonDialog.assessmentId, reason) : undefined)}
        onCancel={() => setReasonDialog(null)}
      />

      {/* Leaving with unsaved changes */}
      <ConfirmDialog {...unsavedGuard.dialogProps} />

      {/* Past assessments list view */}
      {activeTab !== 'new' && (
        <GenericListView
          title="Assessments"
          icon={<FileText size={20} />}
          expanded={expanded}
          onToggleExpand={toggleExpanded}
          items={sortedAssessments}
          keyExtractor={(assessment) => assessment.assessmentId}
          columns={[
            {
              key: 'date',
              label: 'Date',
              width: 'w-28',
              render: (assessment) => (
                <span className="text-sm font-medium text-gray-900">
                  {new Date(assessment.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              ),
            },
            {
              key: 'provider',
              label: 'Provider',
              width: 'flex-1 min-w-0',
              render: (assessment) => (
                <span className="text-sm text-gray-700 truncate pr-3">
                  {assessment.providerName}
                </span>
              ),
            },
            {
              key: 'discipline',
              label: 'Discipline',
              width: 'w-28',
              align: 'center',
              render: (assessment) => (
                <DisciplinePill discipline={assessment.discipline} />
              ),
            },
            {
              key: 'type',
              label: 'Type',
              width: 'w-32',
              align: 'center',
              render: (assessment) => (
                <span
                  className={`px-3 py-1 rounded text-xs font-semibold ${getAssessmentTypeColor(
                    assessment.assessmentType
                  )}`}
                >
                  {getAssessmentTypeDisplayText(assessment.assessmentType)}
                </span>
              ),
            },
            {
              key: 'status',
              label: 'Status',
              width: 'w-24',
              align: 'center',
              render: (assessment) => (
                <DocStatusPill status={assessment.status} />
              ),
            },
          ]}
          actions={[
            {
              icon: <FileDown size={14} />,
              label: 'Download PDF',
              onClick: (a) => { if (a.assessmentId) handleDownloadAssessmentPdf(a.assessmentId); },
              className: 'p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded transition-colors',
            },
            {
              icon: <CopyPlus size={14} />,
              label: 'Start reassessment from this one',
              onClick: (a) => { handleStartReassessment(a); },
              isVisible: (a) => !isReadOnlyView && a.status === AssessmentStatus.COMPLETE,
              className: 'p-1.5 text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded transition-colors',
            },
            {
              icon: <Edit size={14} />,
              label: isReadOnlyView ? 'View assessment' : 'Edit assessment',
              onClick: (assessment) => handleEditAssessment(assessment.assessmentId),
              isVisible: () => canReadAssessment,
              className: isReadOnlyView 
                ? 'p-1.5 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors'
                : 'p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors',
            },
            {
              icon: <RotateCcw size={14} />,
              label: 'Reopen assessment',
              onClick: (assessment) => setReasonDialog({
                kind: assessment.status === AssessmentStatus.COMPLETE ? 'reopen' : 'reject',
                assessmentId: assessment.assessmentId,
              }),
              isVisible: (assessment) =>
                !isReadOnlyView && canQAAssessment && (
                  assessment.status === AssessmentStatus.SUBMITTED ||
                  assessment.status === AssessmentStatus.QA ||
                  assessment.status === AssessmentStatus.COMPLETE
                ),
              className: 'p-1.5 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded transition-colors',
            },
          ]}
          mobileCard={{
            primaryText: (assessment) => assessment.providerName,
            secondaryText: (assessment) =>
              new Date(assessment.date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              }),
            badges: (assessment) =>
              [
                {
                  label: mapDisciplineToCategory(assessment.discipline),
                  className: getDisciplinePillClassName(assessment.discipline),
                },
                {
                  label: getAssessmentTypeDisplayText(assessment.assessmentType) || '',
                  className: getAssessmentTypeColor(assessment.assessmentType),
                },
                {
                  label: getDocStatusConfig(assessment.status).label,
                  className: getDocStatusPillClassName(assessment.status),
                },
              ].filter((badge) => badge.label),
            actions: (assessment) => [
              {
                icon: <Edit size={18} />,
                label: isReadOnlyView ? 'View assessment' : 'Edit assessment',
                onClick: () => handleEditAssessment(assessment.assessmentId),
                className: isReadOnlyView
                  ? 'p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors'
                  : 'p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors',
              },
              ...(canReadAssessment && !isReadOnlyView && canQAAssessment && (
                assessment.status === AssessmentStatus.SUBMITTED ||
                assessment.status === AssessmentStatus.QA ||
                assessment.status === AssessmentStatus.COMPLETE
              )
                ? [
                    {
                      icon: <RotateCcw size={18} />,
                      label: 'Reopen assessment',
                      onClick: () => setReasonDialog({
                        kind: assessment.status === AssessmentStatus.COMPLETE ? 'reopen' : 'reject',
                        assessmentId: assessment.assessmentId,
                      }),
                      className:
                        'p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors',
                    },
                  ]
                : []),
            ],
          }}
          onRefresh={handleRefreshAssessments}
          refreshing={refreshing}
          loading={loading}
          newButtonLabel="New Assessment"
          newButtonOptions={assessmentTypeOptions}
          showNew={canCreateAssessment}
          hasMore={hasMoreAssessments}
          onLoadMore={handleLoadMoreAssessments}
          loadingMore={loadingMore}
          error={error}
          onDismissError={() => setError(null)}
          hasLoadedOnce={hasLoadedOnce}
          emptyStateIcon={<FileText size={48} className="text-gray-300" />}
          emptyStateTitle="No assessments available"
          emptyStateMessage="Start by creating a new assessment using the button above."
          showList={activeTab === 'past'}
        >
          {/* Assessment Type Selection View */}
          {activeTab === 'select_type' && renderAssessmentTypeSelection()}
        </GenericListView>
      )}

      {/* New/Edit assessment form — FormPageLayout */}
      {activeTab === 'new' && (
        <FormPageLayout
          onBack={handleCancelForm}
          actions={buildAssessmentActions()}
          title={getFormTitle()}
          subtitle={formSubtitle}
          headerRight={formHeaderRight}
        >
          <div {...dirty.captureProps}>
            {renderAssessmentForm()}
          </div>
        </FormPageLayout>
      )}
    </>
  );
};

export default Assessments;